import { useEffect } from 'react';
import { generatePath } from 'react-router-dom';
import { DailyScheduleAddEditForm } from 'components/templates';
import { useJournalId } from 'hooks/useJournalId/useJournalId';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useJournalDetailsBreadcrumb } from 'pages/app/staff/journal/journalDetails/hooks';
import { DailyScheduleFormFields } from 'types';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { formFields } from 'consts/form/formFields';
import { appPaths } from 'consts/paths/paths';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { ErrorDisplay } from 'components/molecules';
import { parseDailyScheduleFormFieldsIntoAddUpdateDailyScheduleDTO } from 'utils/parsers/schedule/parseDailyScheduleFormFieldsIntoAddUpdateDailyScheduleDTO';
import { parseDailyScheduleDTOIntoDailyScheduleFormFields } from 'utils/parsers/schedule/parseDailyScheduleDTOIntoDailyScheduleFormFields';
import { journalTexts } from 'consts/text';
import { useEditForm } from 'hooks/useEditForm/useEditForm';

import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';

export const GroupJournalDailyScheduleEditPage = () => {
    const { form, formInitialData, setFormData } = useEditForm<DailyScheduleFormFields>();
    const { journalId } = useJournalId();
    const { displaySuccess } = useSimpleNotification();
    const { statefulNavigate } = useNavigateWithState();

    useJournalDetailsBreadcrumb({
        journalId,
        journalType: 'GROUP',
    });

    const {
        data: dailyScheduleData,
        isFetching: isDailyScheduleDataFetching,
        isError: isDailyScheduleDataError,
    } = useAppQuery('DAILY_SCHEDULE', [journalId], () =>
        StaffHTTPService.schedules.dailySchedule.getDailySchedule(journalId),
    );

    useEffect(() => {
        if (!dailyScheduleData || dailyScheduleData.description) {
            return;
        }
        statefulNavigate(
            generatePath(
                appPaths.app.journals.groupJournalDetails.pickedJournal.schedules.daily.add,
                {
                    journalId,
                },
            ),
            { replace: true },
        );
    }, [dailyScheduleData, dailyScheduleData?.description, journalId, statefulNavigate]);

    useEffect(() => {
        if (dailyScheduleData && !form.getFieldValue(formFields.description)) {
            setFormData(parseDailyScheduleDTOIntoDailyScheduleFormFields(dailyScheduleData));
        }
    }, [dailyScheduleData, form, setFormData]);

    const onEditDailyScheduleSuccess = () => {
        displaySuccess(
            journalTexts.journalDetails.groupJournal.schedules.dailySchedule
                .successfullyUpdatedDailySchedule,
        );
        statefulNavigate(
            generatePath(
                appPaths.app.journals.groupJournalDetails.pickedJournal.schedules.daily.base,
                { journalId },
            ),
            { replace: true },
        );
    };

    const {
        mutate: updateDailySchedule,
        error: updateDailyScheduleError,
        isLoading: isUpdateDailyScheduleLoading,
    } = useAppMutation(StaffHTTPService.schedules.dailySchedule.updateDailySchedule, {
        key: ['UPDATE_DAILY_SCHEDULE'],
        onSuccess: onEditDailyScheduleSuccess,
        invalidateQueryKeys: [['DAILY_SCHEDULE', journalId]],
    });

    const onEditDailyScheduleFinish = (dailyScheduleFormData: DailyScheduleFormFields) => {
        const parsedUpdateDailyScheduleDTO =
            parseDailyScheduleFormFieldsIntoAddUpdateDailyScheduleDTO(dailyScheduleFormData);
        updateDailySchedule({ journalId, addUpdateDailyScheduleDTO: parsedUpdateDailyScheduleDTO });
    };

    if (isDailyScheduleDataError) {
        return <ErrorDisplay />;
    }

    return (
        <DailyScheduleAddEditForm
            mode="edit"
            form={form}
            initialValues={formInitialData}
            axiosError={updateDailyScheduleError}
            isLoading={isDailyScheduleDataFetching || isUpdateDailyScheduleLoading}
            onFinish={onEditDailyScheduleFinish}
        />
    );
};
